import Cookies from 'js-cookie'
import { logAnonEvent, logEvent } from './logEvent'

const isLoggedIn = () => !!Cookies.get('session_hint')

// Extract the best human-readable label from a DOM element.
// Priority: data-log-action > aria-label > title > text content > placeholder > name > id > tag
const getLabel = (el) => {
  if (!el) return 'unknown'
  const candidates = [
    el.dataset?.logAction,
    el.getAttribute?.('aria-label'),
    el.title,
    el.textContent?.trim().replace(/\s+/g, ' ').slice(0, 80),
    el.placeholder,
    el.name,
    el.id,
    el.tagName?.toLowerCase(),
  ]
  return candidates.find((c) => c && c.trim().length > 0) || 'unknown'
}

const getPage = () => window.location.pathname + window.location.search

// ── Click tracking ────────────────────────────────────────────────────────────
// Uses capture phase (third arg = true) so it fires even if a handler calls
// stopPropagation inside the component.
const onDocumentClick = (e) => {
  const target = e.target.closest(
    'button, a[href], input[type="button"], input[type="submit"], input[type="checkbox"], input[type="radio"], [role="button"], [data-log-action]'
  )
  if (!target) return

  const label = getLabel(target)
  // Skip if no useful label was found or it is a lone SVG/icon char
  if (!label || label === 'unknown') return

  const payload = {
    action: `click_${target.tagName.toLowerCase()}`,
    content: JSON.stringify({ label, page: getPage() }),
  }
  isLoggedIn() ? logEvent(payload) : logAnonEvent(payload)
}

// ── Input tracking (debounced — logs after user pauses, never logs the value) ─
let inputTimer = null

const onDocumentInput = (e) => {
  const target = e.target
  if (!['INPUT', 'TEXTAREA'].includes(target.tagName)) return
  // Never log password / card fields
  if (['password', 'tel', 'number'].includes(target.type)) return

  clearTimeout(inputTimer)
  inputTimer = setTimeout(() => {
    const field =
      target.dataset?.logField ||
      target.getAttribute?.('aria-label') ||
      target.placeholder ||
      target.name ||
      target.id ||
      target.type ||
      'field'

    const payload = {
      action: 'input_field',
      content: JSON.stringify({ field, page: getPage(), chars: target.value?.length ?? 0 }),
    }
    isLoggedIn() ? logEvent(payload) : logAnonEvent(payload)
  }, 1500)
}

// ── Select / dropdown change tracking ────────────────────────────────────────
const onDocumentChange = (e) => {
  const target = e.target
  if (target.tagName !== 'SELECT') return

  const field = target.dataset?.logField || target.name || target.id || 'select'
  const selected = target.options?.[target.selectedIndex]?.text || target.value

  const payload = {
    action: 'select_option',
    content: JSON.stringify({ field, selected, page: getPage() }),
  }
  isLoggedIn() ? logEvent(payload) : logAnonEvent(payload)
}

// ── Mount / unmount ───────────────────────────────────────────────────────────
export const initBehaviorTracker = () => {
  document.addEventListener('click', onDocumentClick, true)
  document.addEventListener('input', onDocumentInput, { passive: true })
  document.addEventListener('change', onDocumentChange, { passive: true })

  return () => {
    document.removeEventListener('click', onDocumentClick, true)
    document.removeEventListener('input', onDocumentInput)
    document.removeEventListener('change', onDocumentChange)
    clearTimeout(inputTimer)
  }
}
